import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Switch } from '../components/ui/switch';
import { Cpu, Plus, Pencil, Trash2, RefreshCw, Wifi, WifiOff, Zap, TrendingUp, Lock, Info, RotateCcw, GitBranch } from 'lucide-react';
import { toast } from 'sonner';

export default function Devices() {
    const { api, isAdmin } = useAuth();
    const [devices, setDevices] = useState([]);
    const [areas, setAreas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingDevice, setEditingDevice] = useState(null);
    const [refreshingId, setRefreshingId] = useState(null);
    
    const [formData, setFormData] = useState({
        name: '',
        shelly_device_id: '',
        device_type: 'shelly_em',
        measurement_type: 'consumption',
        channel_mode: 'channel_0',
        invert_signal: false,
        // 3EM specific fields
        invert_l1: false,
        invert_l2: false,
        invert_l3: false,
        area_id: '',
        parent_device_id: ''  // Sub-metering hierarchy
    });

    const CHANNEL_MODE_OPTIONS = [
        { value: 'channel_0', label: 'Apenas Canal 1 (Pinça 1)' },
        { value: 'channel_1', label: 'Apenas Canal 2 (Pinça 2)' },
        { value: 'sum', label: 'Soma dos Canais (Pinça 1 + Pinça 2)' },
        { value: 'average', label: 'Média dos Canais' }
    ];

    const fetchData = async () => {
        try {
            const [devicesRes, areasRes] = await Promise.all([
                api.get('/devices'),
                api.get('/areas')
            ]);
            setDevices(devicesRes.data);
            setAreas(areasRes.data);
        } catch (error) {
            console.error('Error fetching devices:', error);
            toast.error('Erro ao carregar dispositivos');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const resetForm = () => {
        setFormData({
            name: '',
            shelly_device_id: '',
            device_type: 'shelly_em',
            measurement_type: 'consumption',
            channel_mode: 'channel_0',
            invert_signal: false,
            // 3EM specific fields
            invert_l1: false,
            invert_l2: false,
            invert_l3: false,
            area_id: '',
            parent_device_id: ''  // Sub-metering hierarchy
        });
        setEditingDevice(null);
    };

    const handleOpenDialog = (device = null) => {
        if (device) {
            setEditingDevice(device);
            // Normalize old device types to new format
            let deviceType = device.device_type || device.type || 'shelly_em';
            if (deviceType === 'em') deviceType = 'shelly_em';
            if (deviceType === '3em') deviceType = 'shelly_3em';
            
            setFormData({
                name: device.name,
                shelly_device_id: device.shelly_device_id,
                device_type: deviceType,
                measurement_type: device.measurement_type,
                channel_mode: device.channel_mode || 'sum',
                invert_signal: device.invert_signal || false,
                // 3EM specific fields
                invert_l1: device.invert_l1 || false,
                invert_l2: device.invert_l2 || false,
                invert_l3: device.invert_l3 || false,
                area_id: device.area_id || '',
                parent_device_id: device.parent_device_id || ''  // Sub-metering hierarchy
            });
        } else {
            resetForm();
        }
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        resetForm();
    };
    
    // Helper to check if device is 3-phase
    const is3Phase = (deviceType) => {
        return deviceType === 'shelly_3em' || deviceType === 'shelly_pro_3em' || deviceType === '3em';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            const payload = {
                ...formData,
                area_id: formData.area_id || null
            };

            if (editingDevice) {
                const updatePayload = {
                    name: formData.name,
                    measurement_type: formData.measurement_type,
                    area_id: formData.area_id || null,
                    parent_device_id: formData.parent_device_id || null  // Sub-metering hierarchy
                };
                
                // Add type-specific fields based on whether it's 3-phase
                const deviceType = editingDevice.device_type || editingDevice.type || 'shelly_em';
                if (is3Phase(deviceType)) {
                    updatePayload.invert_l1 = formData.invert_l1;
                    updatePayload.invert_l2 = formData.invert_l2;
                    updatePayload.invert_l3 = formData.invert_l3;
                } else {
                    updatePayload.channel_mode = formData.channel_mode;
                    updatePayload.invert_signal = formData.invert_signal;
                }
                
                await api.put(`/devices/${editingDevice.id}`, updatePayload);
                toast.success('Dispositivo atualizado com sucesso');
            } else {
                await api.post('/devices', payload);
                toast.success('Dispositivo adicionado com sucesso');
            }
            
            handleCloseDialog();
            fetchData();
        } catch (error) {
            console.error('Error saving device:', error);
            toast.error(error.response?.data?.detail || 'Erro ao guardar dispositivo');
        }
    };

    const handleDelete = async (deviceId) => {
        try {
            await api.delete(`/devices/${deviceId}`);
            toast.success('Dispositivo eliminado com sucesso');
            fetchData();
        } catch (error) {
            console.error('Error deleting device:', error);
            toast.error(error.response?.data?.detail || 'Erro ao eliminar dispositivo');
        }
    };

    const handleRefresh = async (deviceId) => {
        setRefreshingId(deviceId);
        try {
            await api.post(`/devices/${deviceId}/refresh`);
            toast.success('Estado do dispositivo atualizado');
            fetchData();
        } catch (error) {
            console.error('Error refreshing device:', error);
            toast.error('Erro ao atualizar estado do dispositivo');
        } finally {
            setRefreshingId(null);
        }
    };

    const formatPower = (value) => {
        if (!value) return '0 W';
        if (value >= 1000) return `${(value / 1000).toFixed(2)} kW`;
        return `${value.toFixed(0)} W`;
    };

    const getAreaName = (areaId) => {
        const area = areas.find(a => a.id === areaId);
        return area?.name || 'Sem área';
    };

    if (loading) {
        return (
            <div className="space-y-6" data-testid="devices-loading">
                <div className="flex justify-between items-center">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-10 w-40" />
                </div>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {[...Array(6)].map((_, i) => (
                        <Skeleton key={i} className="h-48 rounded-lg" />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="devices-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold font-heading text-slate-900">Dispositivos</h1>
                    <p className="text-slate-500">
                        {isAdmin 
                            ? 'Gerencie os dispositivos Shelly EM e 3EM' 
                            : 'Visualize os dispositivos Shelly EM e 3EM da empresa'}
                    </p>
                </div>
                {isAdmin && (
                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                        <DialogTrigger asChild>
                            <Button
                                className="bg-emerald-600 hover:bg-emerald-700"
                                onClick={() => handleOpenDialog()}
                                data-testid="add-device-btn"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Adicionar Dispositivo
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-md">
                            <DialogHeader>
                                <DialogTitle className="font-heading">
                                    {editingDevice ? 'Editar Dispositivo' : 'Adicionar Dispositivo'}
                                </DialogTitle>
                                <DialogDescription>
                                    {editingDevice ? 'Atualize os dados do dispositivo' : 'Configure um novo dispositivo Shelly'}
                                </DialogDescription>
                            </DialogHeader>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="name">Nome do Dispositivo</Label>
                                    <Input
                                        id="name"
                                        placeholder="Ex: Máquina CNC 1"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        required
                                        data-testid="device-name-input"
                                    />
                                </div>
                                
                                {!editingDevice && (
                                    <div className="space-y-2">
                                        <Label htmlFor="shelly_device_id">ID do Dispositivo Shelly</Label>
                                        <Input
                                            id="shelly_device_id"
                                            placeholder="Ex: shellyem-XXXXXX"
                                            value={formData.shelly_device_id}
                                            onChange={(e) => setFormData({ ...formData, shelly_device_id: e.target.value })}
                                            required
                                            data-testid="device-shelly-id-input"
                                        />
                                        <p className="text-xs text-slate-500">
                                            Encontra este ID na app Shelly ou no dispositivo
                                        </p>
                                    </div>
                                )}

                                {!editingDevice && (
                                    <div className="space-y-2">
                                        <Label>Tipo de Dispositivo</Label>
                                        <Select
                                            value={formData.device_type}
                                            onValueChange={(value) => setFormData({ ...formData, device_type: value })}
                                        >
                                            <SelectTrigger data-testid="device-type-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="shelly_em">Shelly EM (Monofásico - 2 pinças)</SelectItem>
                                                <SelectItem value="shelly_3em">Shelly 3EM (Trifásico)</SelectItem>
                                                <SelectItem value="shelly_pro_em">Shelly Pro EM (Gen2 - Monofásico)</SelectItem>
                                                <SelectItem value="shelly_pro_3em">Shelly Pro 3EM (Gen2 - Trifásico)</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <p className="text-xs text-slate-500">
                                            {formData.device_type.includes('pro') ? 
                                                'Dispositivos Pro usam API Gen2 com formato de dados diferente' : 
                                                'Dispositivos standard usam API Gen1'}
                                        </p>
                                    </div>
                                )}

                                <div className="space-y-2">
                                    <Label>Tipo de Medição</Label>
                                    <Select
                                        value={formData.measurement_type}
                                        onValueChange={(value) => setFormData({ ...formData, measurement_type: value })}
                                    >
                                        <SelectTrigger data-testid="measurement-type-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="consumption">Consumo</SelectItem>
                                            <SelectItem value="production">Produção</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Shelly EM / Pro EM (monofásico) - Modo de Canais */}
                                {(formData.device_type === 'shelly_em' || formData.device_type === 'shelly_pro_em') && (
                                    <>
                                        <div className="space-y-2">
                                            <Label>Modo de Canais (Pinças)</Label>
                                            <Select
                                                value={formData.channel_mode}
                                                onValueChange={(value) => setFormData({ ...formData, channel_mode: value })}
                                            >
                                                <SelectTrigger data-testid="channel-mode-select">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {CHANNEL_MODE_OPTIONS.map((option) => (
                                                        <SelectItem key={option.value} value={option.value}>
                                                            {option.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            <p className="text-xs text-slate-500">
                                                Define como os valores das pinças do Shelly EM são calculados
                                            </p>
                                        </div>

                                        <div className="flex items-center justify-between p-3 border rounded-lg bg-slate-50">
                                            <div className="space-y-0.5">
                                                <Label htmlFor="invert_signal" className="flex items-center gap-2">
                                                    <RotateCcw className="w-4 h-4 text-slate-500" />
                                                    Inverter Sinal
                                                </Label>
                                                <p className="text-xs text-slate-500">
                                                    Ative se as pinças estiverem instaladas de forma invertida
                                                </p>
                                            </div>
                                            <Switch
                                                id="invert_signal"
                                                checked={formData.invert_signal}
                                                onCheckedChange={(checked) => setFormData({ ...formData, invert_signal: checked })}
                                                data-testid="invert-signal-switch"
                                            />
                                        </div>
                                    </>
                                )}

                                {/* Shelly 3EM / Pro 3EM (trifásico) - Inversão por fase */}
                                {(formData.device_type === 'shelly_3em' || formData.device_type === 'shelly_pro_3em') && (
                                    <div className="space-y-3 p-3 border rounded-lg bg-slate-50">
                                        <div className="flex items-center gap-2 mb-2">
                                            <RotateCcw className="w-4 h-4 text-slate-500" />
                                            <Label className="font-medium">Inversão de Sinal por Fase</Label>
                                        </div>
                                        <p className="text-xs text-slate-500 mb-3">
                                            Ative as fases cujas pinças estiverem instaladas de forma invertida
                                        </p>
                                        
                                        <div className="grid grid-cols-3 gap-3">
                                            <div className="flex flex-col items-center gap-2 p-2 bg-white rounded-lg border">
                                                <span className="text-sm font-medium text-blue-600">L1</span>
                                                <Switch
                                                    id="invert_l1"
                                                    checked={formData.invert_l1}
                                                    onCheckedChange={(checked) => setFormData({ ...formData, invert_l1: checked })}
                                                    data-testid="invert-l1-switch"
                                                />
                                                <span className="text-xs text-slate-500">Fase 1</span>
                                            </div>
                                            <div className="flex flex-col items-center gap-2 p-2 bg-white rounded-lg border">
                                                <span className="text-sm font-medium text-yellow-600">L2</span>
                                                <Switch
                                                    id="invert_l2"
                                                    checked={formData.invert_l2}
                                                    onCheckedChange={(checked) => setFormData({ ...formData, invert_l2: checked })}
                                                    data-testid="invert-l2-switch"
                                                />
                                                <span className="text-xs text-slate-500">Fase 2</span>
                                            </div>
                                            <div className="flex flex-col items-center gap-2 p-2 bg-white rounded-lg border">
                                                <span className="text-sm font-medium text-red-600">L3</span>
                                                <Switch
                                                    id="invert_l3"
                                                    checked={formData.invert_l3}
                                                    onCheckedChange={(checked) => setFormData({ ...formData, invert_l3: checked })}
                                                    data-testid="invert-l3-switch"
                                                />
                                                <span className="text-xs text-slate-500">Fase 3</span>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="space-y-2">
                                    <Label>Área (opcional)</Label>
                                    <Select
                                        value={formData.area_id || "none"}
                                        onValueChange={(value) => setFormData({ ...formData, area_id: value === "none" ? "" : value })}
                                    >
                                        <SelectTrigger data-testid="device-area-select">
                                            <SelectValue placeholder="Selecione uma área" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">Sem área</SelectItem>
                                            {areas.map((area) => (
                                                <SelectItem key={area.id} value={area.id}>
                                                    {area.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Parent device selection for sub-metering */}
                                <div className="space-y-2">
                                    <Label>Dispositivo Pai (Sub-contagem)</Label>
                                    <Select
                                        value={formData.parent_device_id || "none"}
                                        onValueChange={(value) => setFormData({ ...formData, parent_device_id: value === "none" ? "" : value })}
                                    >
                                        <SelectTrigger data-testid="parent-device-select">
                                            <SelectValue placeholder="Selecione o dispositivo pai" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">Nenhum (Contagem Principal)</SelectItem>
                                            {devices
                                                .filter(d => d.id !== editingDevice?.id && !d.parent_device_id)
                                                .map((device) => (
                                                    <SelectItem key={device.id} value={device.id}>
                                                        {device.name}
                                                    </SelectItem>
                                                ))}
                                        </SelectContent>
                                    </Select>
                                    <p className="text-xs text-slate-500">
                                        Se este dispositivo mede um quadro parcial cujo consumo já está incluído noutro dispositivo, selecione o dispositivo pai. O consumo não será contabilizado duas vezes.
                                    </p>
                                </div>

                                <DialogFooter>
                                    <Button type="button" variant="outline" onClick={handleCloseDialog}>
                                        Cancelar
                                    </Button>
                                    <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="device-save-btn">
                                        {editingDevice ? 'Guardar' : 'Adicionar'}
                                    </Button>
                                </DialogFooter>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            {/* Info alert for regular users */}
            {!isAdmin && (
                <Alert>
                    <Info className="w-4 h-4" />
                    <AlertDescription>
                        Apenas administradores podem adicionar, editar ou eliminar dispositivos. 
                        Contacte o administrador da sua empresa para fazer alterações.
                    </AlertDescription>
                </Alert>
            )}

            {/* Devices Grid */}
            {devices.length === 0 ? (
                <Card className="p-12" data-testid="no-devices">
                    <div className="text-center">
                        <Cpu className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                        <h3 className="text-lg font-semibold text-slate-900 mb-2">Nenhum dispositivo configurado</h3>
                        <p className="text-slate-500 mb-6 max-w-md mx-auto">
                            {isAdmin 
                                ? 'Adicione os seus dispositivos Shelly EM ou 3EM para começar a monitorizar o consumo de energia.'
                                : 'Não existem dispositivos configurados. Contacte o administrador para adicionar dispositivos.'
                            }
                        </p>
                        {isAdmin && (
                            <Button
                                className="bg-emerald-600 hover:bg-emerald-700"
                                onClick={() => handleOpenDialog()}
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Adicionar Primeiro Dispositivo
                            </Button>
                        )}
                    </div>
                </Card>
            ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {devices.map((device) => (
                        <Card key={device.id} className="hover:shadow-md transition-shadow" data-testid={`device-card-${device.id}`}>
                            <CardHeader className="pb-3">
                                <div className="flex items-start justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className={`p-2 rounded-lg ${device.measurement_type === 'production' ? 'bg-emerald-50' : 'bg-blue-50'}`}>
                                            {device.measurement_type === 'production' ? (
                                                <Zap className="w-5 h-5 text-emerald-600" />
                                            ) : (
                                                <TrendingUp className="w-5 h-5 text-blue-600" />
                                            )}
                                        </div>
                                        <div>
                                            <CardTitle className="text-base font-heading">{device.name}</CardTitle>
                                            <CardDescription className="text-xs">
                                                {(device.device_type || device.type || 'EM').toUpperCase().replace('SHELLY_', '')} • {getAreaName(device.area_id)}
                                            </CardDescription>
                                        </div>
                                    </div>
                                    <Badge variant={device.online ? 'default' : 'secondary'} className={device.online ? 'bg-emerald-100 text-emerald-700' : ''}>
                                        {device.online ? (
                                            <><Wifi className="w-3 h-3 mr-1" /> Online</>
                                        ) : (
                                            <><WifiOff className="w-3 h-3 mr-1" /> Offline</>
                                        )}
                                    </Badge>
                                </div>
                                {/* Indicador de inversão para EM (mono) */}
                                {!is3Phase(device.device_type || device.type) && device.invert_signal && (
                                    <div className="flex items-center gap-1.5 mt-2 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-md w-fit">
                                        <RotateCcw className="w-3 h-3" />
                                        <span>Sinal invertido</span>
                                    </div>
                                )}
                                {/* Indicador de inversão para 3EM (tri) */}
                                {is3Phase(device.device_type || device.type) && (device.invert_l1 || device.invert_l2 || device.invert_l3) && (
                                    <div className="flex items-center gap-1.5 mt-2 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-md w-fit">
                                        <RotateCcw className="w-3 h-3" />
                                        <span>Invertido: {[device.invert_l1 && 'L1', device.invert_l2 && 'L2', device.invert_l3 && 'L3'].filter(Boolean).join(', ')}</span>
                                    </div>
                                )}
                                {/* Indicador de sub-contagem (dispositivo filho) */}
                                {device.parent_device_id && (
                                    <div className="flex items-center gap-1.5 mt-2 text-xs text-purple-600 bg-purple-50 px-2 py-1 rounded-md w-fit">
                                        <GitBranch className="w-3 h-3" />
                                        <span>Sub-contagem de: {devices.find(d => d.id === device.parent_device_id)?.name || 'Desconhecido'}</span>
                                    </div>
                                )}
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    {/* Potência total e tensão */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <p className="text-xs text-slate-500 uppercase tracking-wider">Potência Total</p>
                                            <p className="text-xl font-bold text-slate-900 font-heading">
                                                {formatPower(device.last_reading?.power)}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-slate-500 uppercase tracking-wider">Tensão</p>
                                            <p className="text-xl font-bold text-slate-900 font-heading">
                                                {device.last_reading?.voltage?.toFixed(1) || 0} V
                                            </p>
                                        </div>
                                    </div>
                                    
                                    {/* Potências por fase - apenas para 3EM (standard e Pro) */}
                                    {is3Phase(device.device_type || device.type) && device.last_reading && (
                                        <div className="grid grid-cols-3 gap-2 p-2 bg-slate-50 rounded-lg">
                                            <div className="text-center">
                                                <p className="text-xs font-medium text-blue-600">L1</p>
                                                <p className="text-sm font-bold text-slate-700">
                                                    {formatPower(device.last_reading?.power_l1)}
                                                </p>
                                            </div>
                                            <div className="text-center border-x border-slate-200">
                                                <p className="text-xs font-medium text-yellow-600">L2</p>
                                                <p className="text-sm font-bold text-slate-700">
                                                    {formatPower(device.last_reading?.power_l2)}
                                                </p>
                                            </div>
                                            <div className="text-center">
                                                <p className="text-xs font-medium text-red-600">L3</p>
                                                <p className="text-sm font-bold text-slate-700">
                                                    {formatPower(device.last_reading?.power_l3)}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                    
                                    <div className="flex items-center gap-2 pt-2 border-t">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleRefresh(device.id)}
                                            disabled={refreshingId === device.id}
                                            data-testid={`refresh-device-${device.id}`}
                                        >
                                            <RefreshCw className={`w-4 h-4 mr-1 ${refreshingId === device.id ? 'animate-spin' : ''}`} />
                                            Atualizar
                                        </Button>
                                        {isAdmin && (
                                            <>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleOpenDialog(device)}
                                                    data-testid={`edit-device-${device.id}`}
                                                >
                                                    <Pencil className="w-4 h-4 mr-1" />
                                                    Editar
                                                </Button>
                                                <AlertDialog>
                                                    <AlertDialogTrigger asChild>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                            data-testid={`delete-device-${device.id}`}
                                                        >
                                                            <Trash2 className="w-4 h-4 mr-1" />
                                                            Eliminar
                                                        </Button>
                                                    </AlertDialogTrigger>
                                                    <AlertDialogContent>
                                                        <AlertDialogHeader>
                                                            <AlertDialogTitle>Eliminar Dispositivo?</AlertDialogTitle>
                                                            <AlertDialogDescription>
                                                                Esta ação não pode ser revertida. Todos os dados de leituras associados a este dispositivo serão eliminados.
                                                            </AlertDialogDescription>
                                                        </AlertDialogHeader>
                                                        <AlertDialogFooter>
                                                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                                            <AlertDialogAction
                                                                className="bg-red-600 hover:bg-red-700"
                                                                onClick={() => handleDelete(device.id)}
                                                            >
                                                                Eliminar
                                                            </AlertDialogAction>
                                                        </AlertDialogFooter>
                                                    </AlertDialogContent>
                                                </AlertDialog>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}
